import { defaultLoadPatientListSlimModel, PatientListModel } from "@/models/PatientListModel";
import { arrayToBase64, createMobileForUpload, getProjectHash, loadMobileFile, LoadRGFileFromServer, ProjectModel } from "@xlabmedical/implant-planner";
import {
    devLogger,
    error,
    exception,
    Exception,
    getOemSerialNumber,
    ImageFileModel,
    NotificationModel,
    NotificationType,
    replaceSuffix,
    success,
    WorkflowStatusModel,
    useAuth,
} from "@xlabmedical/medic-web-framework";
import {
    CopyFileRequestModel,
    DoctorModel,
    Extra,
    FileElement,
    FileElementMetadata,
    GetNotificationsResponse,
    GetPatientsResponse,
    GetZippedFilesListResponse,
    MediaMessageModel,
    MobileReconstructionResponse,
    ProjectFileType,
    ProjectPresctiptionTypeRequestModel,
    ProjectShareModel,
    ProjectType,
    ReconstructionProjectResponse,
    SetProjectFileEditableAsProRequest,
    JobStatusResponse,
    ServerAPI,
    NotificationChannelType,
    ProjectFileShareModel,
} from "@xlabmedical/medic-api";
import { SegmentationStatus } from "@/models/SegmentationStatus";
import { defineStore } from "pinia";
import {
    WebRgWSChatEvent,
    WebRgWSProjectFileAddedEvent,
    WebRgWSProjectUpdatedEvent,
    WebRgWSWeblinkOrderCreatedEvent,
    WebRgWSWeblinkOrderUpdatedEvent,
} from "@/util/WebRgWebsocketManager";
import { useProductionCenterStore } from "@/stores/ProductionCenterStore";
import { translation } from "@/util/WebRgLocalizationPlugin";
import { GlobalConstants } from "@/constants/GlobalConstants";

export interface CustomFile extends File {
    changeDate: Date;
    id: number;
    type: ProjectFileType;
}

interface SetProjectOwnerPayload {
    projectName: string;
    owner: string;
}

function getPatientDataExtra(patientExtra: Extra) {
    const extra = patientExtra;
    extra.doctorID = patientExtra.doctorID ?? "0";

    if (patientExtra.doctorID === "") {
        // hack for corrupted data
        patientExtra.doctorID = "0";
    }

    return extra;
}

function buildProjectKey(patientName: string | undefined, fileName: string, fileUuid?: string | null, fileId?: number | string | null, extraKey?: string) {
    if (fileUuid && fileUuid.trim().length > 0) {
        return fileUuid;
    }
    if (fileId !== undefined && fileId !== null) {
        return `${patientName ?? ""}:${fileName}:${fileId}`;
    }
    const base = getProjectHash(patientName ?? "", fileName);
    return extraKey ? `${base}:${extraKey}` : base;
}

function normalizeAttachmentBaseName(filename: string) {
    const name = filename ?? "";
    if (name.endsWith(".mobile.json")) {
        return name.slice(0, -".mobile.json".length);
    }
    if (name.endsWith(".thumb")) {
        return name.slice(0, -".thumb".length);
    }
    return name;
}

function createPatientsModel(patient: GetPatientsResponse): PatientListModel {
    return {
        lastProjectStep: patient.lastProjectStep,
        lastProjectStatus: patient.orderInfo ? patient.orderInfo.status : "",
        unreadChatCounts: [],
        patientName: patient.name,
        note: patient.patientData.notes,
        firstName: patient.patientData.firstName,
        lastName: patient.patientData.lastName,
        dateOfBirth: new Date(patient.patientData.dateOfBirth),
        importDate: new Date(patient.createDate),
        lastModified: new Date(patient.changeDate),
        user: patient.owner.name,
        patientId: patient.id.toString(),
        shares: patient.shares,
        hasWeblinkOrder: !!patient.orderInfo,
        extra: getPatientDataExtra(patient.patientData.extra ?? {}),
        mine: patient.mine,
        uuid: patient.uuid,
        orderUnreadNotificationsPerChannel: patient.orderInfo ? patient.orderInfo.orderNotifications : 0,
        projectUnreadNotificationsPerChannel: patient.notifications,
        status: patient.orderInfo ? patient.orderInfo.status : undefined,
        orderStatusColor: patient.orderInfo ? extractColor(patient.orderInfo.statusData) : undefined,
        orderProjectId: patient.orderInfo ? patient.orderInfo.projectId : -1,
        unreadNotifications: (patient.orderInfo ? patient.orderInfo.orderNotifications : 0) + (patient.notifications ?? 0),
        hasAccess: patient.orderInfo ? patient.orderInfo.hasAccess : true,
        foreignOwner: !patient.mine ? patient.owner.name || patient.owner.username : undefined,
    };
}

function transformNotification(projectId: string, notification: GetNotificationsResponse, api: ServerAPI): NotificationModel {
    let imageUrl;
    let text = notification.text;
    if (notification.type === NotificationType.MEDIA_MESSAGE) {
        const mediaModel = JSON.parse(notification.text) as MediaMessageModel;
        imageUrl = api.getFileUrl(projectId, mediaModel.imageName);
        text = mediaModel.text;
    }

    return {
        id: notification.id,
        date: new Date(notification.date),
        fromUser: notification.fromUser,
        mine: notification.mine,
        body: text,
        type: notification.type,
        imageUrl: imageUrl,
        title: "",
    };
}

function prepareNotificationsModel(projectId: string, serverNotifications: GetNotificationsResponse[], api: ServerAPI) {
    return serverNotifications.map((el) => {
        return transformNotification(projectId, el, api);
    });
}

export function mapSegmentationStatus(status: string) {
    switch (status) {
        case "FAILED":
            return SegmentationStatus.FAILED;
        case "IN_PROCESS":
            return SegmentationStatus.IN_PROCESS;
        case "COMPLETED":
            return SegmentationStatus.COMPLETED;
        case "ERROR":
            return SegmentationStatus.ERROR;
        default:
            return SegmentationStatus.UNKNOWN;
    }
}

function findSegmentationFile(patientProcessingDataFiles: FileElement[], projectFile: FileElement) {
    let segmentationFile: FileElement | undefined = undefined;
    for (const processingDataFile of patientProcessingDataFiles) {
        if (!processingDataFile.metadata) continue;
        const metadata = JSON.parse(processingDataFile.metadata) as FileElementMetadata;
        if (!metadata) continue;

        if (metadata["job-type"] == "SEGMENTATION" && metadata["origin-file-id"] == projectFile.id) {
            segmentationFile = processingDataFile;
            break;
        }
    }
    return segmentationFile;
}

export function extractColor(jsonData: string): string | undefined {
    try {
        const parsed = JSON.parse(jsonData) as { color?: string };
        return parsed.color ?? undefined;
    } catch {
        return undefined;
    }
}

function normalizeVersionCompany(value?: string): string | undefined {
    if (typeof value !== "string") {
        return undefined;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return undefined;
    }

    const lower = trimmedValue.toLowerCase();
    if (lower.includes("zimmer")) {
        return "ZimVie";
    }
    if (lower === "realguide" || lower === "default") {
        return "3Diemme";
    }

    return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
}

function matchesCurrentVendor(projectVersionCompany?: string, currentVendor?: string | null) {
    const normalizedProjectVersionCompany = normalizeVersionCompany(projectVersionCompany);
    const normalizedCurrentVendor = normalizeVersionCompany(currentVendor ?? undefined);

    if (!normalizedProjectVersionCompany || !normalizedCurrentVendor) {
        return true;
    }

    return normalizedProjectVersionCompany === normalizedCurrentVendor;
}

function extractProjectMetadataExtras(
    metadata?: string,
    defaultVendor?: string,
): {
    versionCompany?: string;
    format: string;
} {
    if (!metadata) {
        return { format: "RealGUIDE 5" };
    }

    try {
        const parsed = JSON.parse(metadata) as {
            versionCompany?: string;
            format?: string;
            extra?: { versionCompany?: string; format?: string };
        };
        const rawVersionCompany = parsed.extra?.versionCompany ?? parsed.versionCompany ?? defaultVendor;
        const versionCompany = normalizeVersionCompany(rawVersionCompany);
        const rawFormat = parsed.extra?.format ?? parsed.format;
        const normalizedFormat = rawFormat?.trim() ?? "";
        const format = normalizedFormat ? (normalizedFormat.toLowerCase() === "json" ? "RealGUIDE Web" : normalizedFormat) : "RealGUIDE 5";
        return { versionCompany, format };
    } catch {
        return { format: "RealGUIDE 5" };
    }
}

export const usePatientsStore = defineStore("patientsStore", {
    state: () => ({
        loading: false,
        patients: [] as PatientListModel[],
        projectsModel: [] as ProjectModel[],
        legacyProjectsModel: [] as ProjectModel[],
        selectedPatientProjects: [] as ProjectModel[],
        selectedPatientLegacyProjects: [] as ProjectModel[],
        selectedPatientNotifications: [] as NotificationModel[],
        selectedPatientAttachemnts: [] as ProjectModel[],
        projectFileModel: [] as ImageFileModel[],
        attachmentsFileModel: [] as ImageFileModel[],
        selectedPatientFileModel: [] as ImageFileModel[],
        doctors: [] as DoctorModel[],
        totalPatientCount: 0,
        selectedPatientFull: null as GetPatientsResponse | null,
        vendorName: null as string | null,
        vendorNamePromise: null as Promise<string | undefined> | null,
        pcStatuses: new Map<number, WorkflowStatusModel[]>(),
        patientListQuery: defaultLoadPatientListSlimModel(),
    }),
    getters: {
        isProjectFromCurrentVendor: (state) => (projectVersionCompany?: string) => {
            return matchesCurrentVendor(projectVersionCompany, state.vendorName);
        },
    },
    actions: {
        clearSelectedPatientsAndProjects() {
            this.selectedPatientProjects = [];
            this.selectedPatientLegacyProjects = [];
            this.selectedPatientNotifications = [];
            this.selectedPatientAttachemnts = [];
            this.selectedPatientFileModel = [];
            this.selectedPatientFull = null;
        },
        async ensureVendorName() {
            if (this.vendorName) {
                GlobalConstants.VENDOR_ID = getOemSerialNumber(this.vendorName);
                return this.vendorName;
            }

            const globalVendorName = normalizeVersionCompany(GlobalConstants.VENDOR_NAME);
            if (globalVendorName) {
                this.vendorName = globalVendorName;
                GlobalConstants.VENDOR_ID = getOemSerialNumber(globalVendorName);
                return globalVendorName;
            }

            if (this.vendorNamePromise) {
                return this.vendorNamePromise;
            }

            this.vendorNamePromise = (async () => {
                try {
                    const { data } = await this.api.getVendorInfo();
                    const vendorName = data?.data;
                    const normalizedVendorName = normalizeVersionCompany(vendorName);
                    if (!normalizedVendorName) {
                        return undefined;
                    }

                    this.vendorName = normalizedVendorName;
                    GlobalConstants.VENDOR_NAME = normalizedVendorName;
                    GlobalConstants.VENDOR_ID = getOemSerialNumber(normalizedVendorName);
                    return normalizedVendorName;
                } catch (err) {
                    exception("Error while getting vendor info:", err as Exception);
                    return undefined;
                } finally {
                    this.vendorNamePromise = null;
                }
            })();

            return this.vendorNamePromise;
        },
        async updateProductionCenterStatuses(productionCenterId: number) {
            if (this.pcStatuses.has(productionCenterId)) {
                return this.pcStatuses.get(productionCenterId)!; // already cached
            }
            const { data } = await this.api.getProductionCenterInfo(productionCenterId);
            this.pcStatuses.set(productionCenterId, data.workflowStatuses as WorkflowStatusModel[]);
            return this.pcStatuses.get(productionCenterId)!;
        },
        async loadPatientsSlim(loadPatientsModel?: Partial<LoadPatientListSlimModel>) {
            const mergedModel: LoadPatientListSlimModel = {
                ...this.patientListQuery,
                ...loadPatientsModel,
            };

            this.patientListQuery = mergedModel;

            const { page, itemsPerPage, search, sortColumn, sortDirection, filterPatients, modifiedFrom, modifiedTo } = mergedModel;

            this.loading = true;

            try {
                const { data: patientsResponse } = await this.api.getPatientsSlim(
                    "",
                    itemsPerPage,
                    page,
                    sortColumn,
                    sortDirection,
                    filterPatients,
                    search,
                    modifiedFrom ?? undefined,
                    modifiedTo ?? undefined,
                );

                this.patients = patientsResponse.data.map((patient: GetPatientsResponse) => createPatientsModel(patient));
                this.totalPatientCount = patientsResponse.meta?.count ?? patientsResponse.data.length;
            } catch (err) {
                devLogger.error("loadPatientsSlim err: ", err);
            } finally {
                this.loading = false;
            }
        },
        async updatePatientNotifications(event: WebRgWSWeblinkOrderUpdatedEvent | WebRgWSChatEvent) {
            if (event.projectId) {
                const projectId = event.projectId.toString();
                const patient = this.patients.find((p) => p.patientId === projectId);

                if (patient) {
                    patient.projectUnreadNotificationsPerChannel++;
                } else {
                    const getUnreadCount = (value: number | Record<string, number | null | undefined> | null | undefined) => {
                        if (typeof value === "number") return value;
                        if (value && typeof value === "object") {
                            return Object.values(value).reduce<number>((sum, item) => sum + (typeof item === "number" ? item : 0), 0);
                        }
                        return 0;
                    };

                    const orderProjects = this.projectsModel.filter((p) => p.weblinkOrderData?.projectId === event.projectId);
                    if (orderProjects.length > 0) {
                        const affectedPatientIds = new Set<string>();

                        orderProjects.forEach((project) => {
                            if (project.patientId) {
                                affectedPatientIds.add(project.patientId);
                            }
                            if (project.weblinkOrderData) {
                                project.weblinkOrderData.unreadNotificationsPerChannel = getUnreadCount(project.weblinkOrderData.unreadNotificationsPerChannel) + 1;
                            }
                        });

                        affectedPatientIds.forEach((patientId) => {
                            const affectedPatient = this.patients.find((p) => p.patientId === patientId);
                            if (affectedPatient) {
                                affectedPatient.orderUnreadNotificationsPerChannel++;
                            }
                        });
                    } else {
                        const weblinkPatientMatch = this.patients.find((p) => p.orderProjectId === event.projectId);
                        if (weblinkPatientMatch) {
                            weblinkPatientMatch.orderUnreadNotificationsPerChannel++;
                        }
                    }
                }
            }
        },
        async updateSingleProject(event: WebRgWSProjectUpdatedEvent | WebRgWSProjectFileAddedEvent) {
            const { data } = event;

            if (data) {
                const project = this.projectsModel.find((p) => p.patientId.toString() === data.id.toString());

                if (project) {
                    // project.shares = [
                    //     {
                    //         "withUser": {
                    //             "username": "pl_test2",
                    //             "name": "pl_test2",
                    //             "colorCode": "#6666bb",
                    //             "operator": false
                    //         },
                    //         "date": "2025-09-29T08:45:33Z",
                    //         "editable": true,
                    //         "anonymized": true,
                    //         "editableWithStart": false
                    //     }
                    // ];

                    project.shares = data.shares;
                }
            }
        },
        async updateSinglePatient(event: WebRgWSWeblinkOrderUpdatedEvent | WebRgWSWeblinkOrderCreatedEvent) {
            const { data, projectId } = event;

            const patient = this.patients.find((p) => p.patientName === data.originProjectReference);

            if (patient) {
                patient.lastProjectStatus = data.status ?? "Open";
                patient.status = data.status ?? "Open";
                patient.orderStatusColor = extractColor(data.statusData) || "";
                patient.lastModified = new Date(data.lastModified);
            }
        },
        async updateSinglePatientOrder(event: WebRgWSWeblinkOrderUpdatedEvent | WebRgWSChatEvent) {
            const { data, projectId } = event;

            const pcStatuses = await this.updateProductionCenterStatuses(data.productionCenterId);

            const projects = this.projectsModel.filter((p) => p.weblinkOrderData?.projectId === projectId);

            for (let i = 0; i < projects.length; i++) {
                const p = projects[i];
                if (!p.weblinkOrderData) continue;

                p.weblinkOrderData.status = data.status;
                p.weblinkOrderData.statusColor = extractColor(data.statusData ?? "") || "";
                p.weblinkOrderData.step = data.step;
                p.weblinkOrderData.allowsUserEditing = data.allowsUserEditing;
                p.weblinkOrderData.awaitUserConfirmation = !!pcStatuses.find((s) => s.id === data.statusId)?.awaitUserConfirmation;

                if (i === 0) {
                    p.lastModified = new Date(data.lastModified);
                }
            }

            const patient = this.patients.find((p) => p.patientName === data.originProjectReference);

            if (patient) {
                patient.lastProjectStatus = data.status ?? "Open";
                patient.status = data.status ?? "Open";
                patient.orderStatusColor = extractColor(data.statusData) || "";
                patient.lastModified = new Date(data.lastModified);
            }
        },

        async updateProjectSegmentationStatus(projectFileUuid: string, status: SegmentationStatus, invokedByUser?: boolean) {
            const projectModel = this.projectsModel.find((el) => el.projectFileUuid === projectFileUuid);

            if (projectModel) {
                projectModel.segmentationStatus = status;
                if (invokedByUser) {
                    projectModel.segmentationInvokedByUser = true;
                    devLogger.debug(
                        "SegmentationStatusUpdate after invoke by user",
                        invokedByUser,
                        ", mobileFileData",
                        projectModel.mobileFile.data["SEGMENTATION_INVOKED_BY_USER"],
                    );
                    const enc = new TextEncoder();
                    projectModel.mobileFile.data["SEGMENTATION_INVOKED_BY_USER"] = {
                        objectType: "value",
                        type: "string",
                        data: enc.encode("1"),
                    };
                    const pName = projectModel.projectFilename.replace(".zip", "");
                    devLogger.debug("Project name: ", pName, "patient:", projectModel.patientName);
                    const zipMobile = await createMobileForUpload(pName, { mobile: projectModel.mobileFile });
                    try {
                        await this.api.uploadFile(projectModel.patientName, pName + ".zip.mobile.json", "mobile", zipMobile);
                        await this.api.uploadProjectPartsBatch(projectModel.patientName, pName, [
                            {
                                part_name: pName,
                                file_type: "mobile.json",
                                file: zipMobile,
                            },
                        ]);
                    } catch (err) {
                        devLogger.warn("Failed to update segmentation status for project " + pName, err);
                    }
                }
            }
        },

        async addProjectFromWS(eventFile, event) {
            if (!event.data || !eventFile || eventFile.type !== ProjectFileType.Mobile) {
                devLogger.warn("Invalid event data addProjectFromWS: ", event);

                return;
            }

            const vendorName = await this.ensureVendorName();
            const resolveWsProjectMetadata = (projectFile: FileElement | undefined, fallbackProject?: Partial<ProjectModel>) => {
                const fallbackFormat = fallbackProject?.format;
                const { versionCompany: extractedVersionCompany, format: extractedFormat } = extractProjectMetadataExtras(
                    projectFile?.metadata,
                    fallbackProject?.versionCompany ?? vendorName ?? undefined,
                );

                const format = extractedFormat === "RealGUIDE 5" && fallbackFormat ? fallbackFormat : extractedFormat;
                const versionCompany = normalizeVersionCompany(extractedVersionCompany ?? fallbackProject?.versionCompany ?? vendorName ?? undefined);

                return { versionCompany, format };
            };

            const project = this.projectsModel.find((p) => p.patientId === event.data.id.toString()); // if is it planner project

            let plannerPatientId = 0;
            if (project === undefined) {
                try {
                    const weblinkOrderProjectFile = this.projectsModel.find((file) => file.weblinkOrderData?.projectId === event.data.id);

                    devLogger.debug("useWebsocket projectFileAddedEventuseWebsocket addProjectFromWS weblinkOrderProjectFile: ", weblinkOrderProjectFile);
                    const fileName = eventFile.name.replace(".zip.mobile.json", ".zip");

                    const _projectFile = event.data.files?.find((p) => p.name === fileName);
                    if (!_projectFile) {
                        devLogger.warn("useWebsocket addProjectFromWS missing project zip file in WS payload", {
                            fileName,
                            eventProjectId: event.data.id,
                            eventProjectFileId: event.projectFileId,
                        });
                        return plannerPatientId;
                    }

                    if (weblinkOrderProjectFile) {
                        const id = buildProjectKey(weblinkOrderProjectFile.patientName, fileName, _projectFile.uuid, _projectFile.id, event.data.id?.toString());
                        const { versionCompany, format } = resolveWsProjectMetadata(_projectFile, weblinkOrderProjectFile);

                        const projectToAdd: ProjectModel = {
                            ...weblinkOrderProjectFile,
                            id,
                            patientId: weblinkOrderProjectFile.patientId,
                            patientName: weblinkOrderProjectFile.weblinkOrderData?.weblinkProjectName,
                            projectFilename: fileName,
                            name: fileName,
                            projectDisplayName: fileName,
                            mobileFilename: eventFile.name,
                            lastModified: new Date(eventFile.file.changeDate),
                            user: eventFile.uploadedBy.name,
                            projectDirty: true,
                            imageLoading: true,
                            isJsonProject: true,
                            segmentationStatus: SegmentationStatus.UNKNOWN,
                            projectUUID: weblinkOrderProjectFile.projectUUID,
                            projectOrigin: "weblink",
                            uuid: _projectFile.uuid,
                            projectFileUuid: _projectFile.uuid,
                            projectFileId: _projectFile.id,
                            editable: eventFile.editable,
                            anonymized: eventFile.anonymized,
                            editableAsPro: eventFile.editableAsPro,
                            editableWithStart: eventFile.editableWithStart,
                            imageBuffer: undefined,
                            implantList: [],
                            versionCompany,
                            format,
                        };

                        if (!this.projectsModel.some((p) => p.id === id)) {
                            devLogger.debug("useWebsocket addProjectFromWS pusham projectToAdd.projectFileId ): ", projectToAdd.projectFileId);
                            devLogger.debug("useWebsocket addProjectFromWS pusham projectToAdd.id ): ", projectToAdd.id);

                            this.projectsModel.push(projectToAdd);

                            await this.loadProjectDataSlim(projectToAdd.id);
                        }

                        this.selectedPatientProjects = this.projectsModel
                            .filter((p) => p.patientId === weblinkOrderProjectFile.patientId)
                            .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());

                        devLogger.debug("useWebsocket addProjectFromWS projectToAdd.id: ", projectToAdd.id);
                        devLogger.debug("useWebsocket addProjectFromWS projectToAdd.projectFileId: ", projectToAdd.projectFileId);
                        plannerPatientId = Number(weblinkOrderProjectFile.patientId);
                    } else {
                        const _patient = this.patients.find((p) => p.patientName === event.data.name);

                        if (!_patient) {
                            return;
                        }

                        const { versionCompany, format } = resolveWsProjectMetadata(_projectFile);

                        const projectToAdd: ProjectModel = {
                            id: _projectFile.uuid,
                            patientId: _patient.patientId,
                            patientName: _patient?.patientName,
                            projectFilename: fileName,
                            name: fileName,
                            projectDisplayName: fileName,
                            mobileFilename: eventFile.name,
                            lastModified: new Date(eventFile.file.changeDate),
                            user: eventFile.uploadedBy.name,
                            projectDirty: true,
                            imageLoading: true,
                            isJsonProject: true,
                            segmentationStatus: SegmentationStatus.UNKNOWN,
                            projectUUID: _patient?.uuid,
                            projectOrigin: "planner",
                            uuid: _projectFile.uuid,
                            projectFileUuid: _projectFile.uuid,
                            projectFileId: _projectFile.id,
                            editable: eventFile.editable,
                            anonymized: eventFile.anonymized,
                            editableAsPro: eventFile.editableAsPro,
                            editableWithStart: eventFile.editableWithStart,
                            imageBuffer: undefined,
                            implantList: [],
                            shares: [],
                            uploadedBy: eventFile.uploadedBy,
                            versionCompany,
                            format,
                        };

                        this.projectsModel.push(projectToAdd);

                        this.selectedPatientProjects = this.projectsModel
                            .filter((p) => p.patientName === event.data.name)
                            .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());

                        await this.loadProjectDataSlim(projectToAdd.id, true);
                    }
                } catch (error) {
                    devLogger.error("useWebsocket projectFileAddedEvent useWebsocket projectFileAddedEvent useWebsocket addProjectFromWS weblinkOrderProjectFile error: ", error);
                }
            } else {
                devLogger.debug("5 useWebsocket projectFileAddedEvent useWebsocket projectFileAddedEvent useWebsocket - eventFile: ", eventFile);
                const fileName = eventFile.name.replace(".zip.mobile.json", ".zip");

                try {
                    const _projectFile = event.data.files?.find((p) => p.name === fileName);
                    if (!_projectFile) {
                        devLogger.warn("useWebsocket addProjectFromWS missing project zip file in WS payload", {
                            fileName,
                            eventProjectId: event.data.id,
                            eventProjectFileId: event.projectFileId,
                        });
                        return plannerPatientId;
                    }
                    const id = buildProjectKey(project.patientName || "", fileName, _projectFile.uuid, _projectFile.id);
                    const { versionCompany, format } = resolveWsProjectMetadata(_projectFile, project);

                    const updatedFields: Partial<ProjectModel> = {
                        id,
                        patientId: project.patientId,
                        patientName: project.patientName,
                        projectFilename: fileName,
                        projectDisplayName: fileName,
                        name: fileName,
                        mobileFilename: eventFile.name,
                        lastModified: new Date(eventFile.file.changeDate),
                        user: eventFile.uploadedBy.name,
                        projectDirty: true,
                        imageLoading: true,
                        isJsonProject: true,
                        segmentationStatus: SegmentationStatus.UNKNOWN,
                        editable: eventFile.editable,
                        anonymized: eventFile.anonymized,
                        editableAsPro: eventFile.editableAsPro,
                        editableWithStart: eventFile.editableWithStart,
                        uuid: _projectFile.uuid,
                        projectFileUuid: _projectFile.uuid,
                        projectFileId: _projectFile.id,
                        imageBuffer: undefined,
                        implantList: [],
                        versionCompany,
                        format,
                    };

                    const existingProject = this.projectsModel.find((p) => p.id === id);

                    if (!existingProject) {
                        const projectToAdd: ProjectModel = {
                            ...eventFile,
                            ...updatedFields,
                            projectUUID: project.projectUUID,
                            projectOrigin: "planner",
                        };

                        devLogger.debug("100 useWebsocket addProjectFromWS projectToAdd: ", projectToAdd);

                        this.projectsModel.push(projectToAdd);
                    } else {
                        devLogger.debug("12 useWebsocket addProjectFromWS updatedFields: ", updatedFields);

                        Object.assign(existingProject, updatedFields);
                    }

                    await this.loadProjectDataSlim(id, true);
                } catch (e) {
                    devLogger.debug("useWebsocket - eventFile UPDATED wrrr: ", e);
                }

                this.selectedPatientProjects = this.projectsModel
                    .filter((p) => p.patientId === project.patientId)
                    .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
            }

            return plannerPatientId;
        },
        async loadProjectDataSlim(projectId: string, fromWs: boolean = false) {
            devLogger.debug("1 loadProjectDataSlim projectId ___:", projectId);
            const projectModel = this.projectsModel.find((el: ProjectModel) => el.id === projectId);

            if ((!projectModel || !projectModel.projectDirty) && !fromWs && !projectModel) return;

            devLogger.debug("2 loadProjectDataSlim projectModel ___ 2 found:", projectModel);

            try {
                this.api.getProjectJobStatusUuid(projectModel.projectFileUuid).then((response: any) => {
                    const filteredRes = response.data.filter((response: JobStatusResponse) => response.type == "SEGMENTATION");

                    if (filteredRes.length === 0) {
                        projectModel.segmentationStatus = SegmentationStatus.NOT_STARTED;
                    } else {
                        projectModel.segmentationStatus = mapSegmentationStatus(filteredRes[0].status);
                    }
                });
            } catch (err) {
                devLogger.error("loadProjectDataSlim err: ", err);
            }

            if (!projectModel.mobileFilename) {
                try {
                    const { data: a } = await this.api.getZippedFilesList(projectModel.patientName, projectModel.projectFilename);
                    const aF = a.filter((file: GetZippedFilesListResponse) => file.fileType === "mobile.json");

                    const { data } = await this.api.getProjectFileJson<ReconstructionProjectResponse>("a", "a", "mobile", aF[0].id.toString());
                    projectModel.mobileFile = await LoadRGFileFromServer(data, projectModel.patientName, projectModel.projectFilename, this.api as ServerAPI);
                    const mobileFileData = loadMobileFile(projectModel.mobileFile);
                    projectModel.implantList = mobileFileData.implants;
                    //projectModel.plannerStep = mobileFileData.plannerStep;
                    projectModel.loadObjectsFromPatient = mobileFileData.loadObjectsFromPatient || false;
                    projectModel.segmentationInvokedByUser = mobileFileData.segmentationInvokedByUser || false;

                    const screenshot = mobileFileData.screenshot;
                    if (screenshot) {
                        projectModel.imageBuffer = arrayToBase64(screenshot);
                    }

                    projectModel.imageLoading = false;
                    projectModel.projectDirty = false;

                    return projectModel.imageBuffer;
                } catch (e) {
                    devLogger.debug(e);
                    projectModel.errorMessage = (e as Error).message;

                    try {
                        const { weblinkProjectName } = projectModel.weblinkOrderData;

                        const { data: a } = await this.api.getZippedFilesList(weblinkProjectName, projectModel.projectFilename);
                        const aF = a.filter((file: GetZippedFilesListResponse) => file.fileType === "mobile.json");

                        const { data } = await this.api.getProjectFileJson<ReconstructionProjectResponse>("a", "a", "mobile", aF[0].id.toString());
                        projectModel.mobileFile = await LoadRGFileFromServer(data, weblinkProjectName, projectModel.projectFilename, this.api as ServerAPI);
                        const mobileFileData = loadMobileFile(projectModel.mobileFile);
                        projectModel.implantList = mobileFileData.implants;
                        //projectModel.plannerStep = mobileFileData.plannerStep;
                        projectModel.loadObjectsFromPatient = mobileFileData.loadObjectsFromPatient || false;
                        projectModel.segmentationInvokedByUser = mobileFileData.segmentationInvokedByUser || false;

                        devLogger.debug("2 loadProjectDataSlim projectModel ___ step 2.2:", projectModel);

                        const screenshot = mobileFileData.screenshot;
                        if (screenshot) {
                            projectModel.imageBuffer = arrayToBase64(screenshot);
                        }
                        projectModel.imageLoading = false;
                        projectModel.projectDirty = false;

                        return projectModel.imageBuffer;
                    } catch (e) {
                        projectModel.errorMessage = "weblinkProjectName: " + projectModel.weblinkOrderData + " " + (e as Error).message;
                        error("Error while loading preview");

                        devLogger.warn("projectModel.mobileFilename not found", e);
                    }
                }
            }

            if (projectModel.mobileFilename) {
                projectModel.imageBuffer = undefined;

                if (projectModel.projectOrigin === "planner") {
                    const t0 = performance.now();
                    devLogger.debug(`[loadProjectDataSlim] (planner) ▶️ start patient=${projectModel.patientName} file=${projectModel.mobileFilename}`);

                    try {
                        // --- fetch JSON ---------------------------------------------------------
                        const tFetch0 = performance.now();
                        const { data } = await this.api.getJsonFile<MobileReconstructionResponse>(projectModel.patientName, projectModel.mobileFilename);

                        if (typeof data !== "object") {
                            throw "Mobile file is not an object";
                        }

                        // --- build RG file ------------------------------------------------------
                        const tBuild0 = performance.now();
                        projectModel.mobileFile = await LoadRGFileFromServer(data, projectModel.patientName, projectModel.projectFilename, this.api as ServerAPI);

                        // --- parse mobile file --------------------------------------------------
                        const tParse0 = performance.now();
                        const mobileFileData = loadMobileFile(projectModel.mobileFile);
                        // projectModel.plannerStep = mobileFileData.plannerStep;
                        projectModel.loadObjectsFromPatient = mobileFileData.loadObjectsFromPatient || false;
                        projectModel.segmentationInvokedByUser = mobileFileData.segmentationInvokedByUser || false;

                        // --- extract implants ---------------------------------------------------
                        const tImpl0 = performance.now();
                        projectModel.implantList = mobileFileData.implants;

                        // --- screenshot -> base64 ----------------------------------------------
                        const screenshot = mobileFileData.screenshot;

                        projectModel.imageBuffer = [];
                        if (screenshot) {
                            const tShot0 = performance.now();
                            projectModel.imageBuffer = arrayToBase64(screenshot);
                        }

                        // --- finalize flags -----------------------------------------------------
                        const tFlags0 = performance.now();
                        projectModel.imageLoading = false;
                        projectModel.projectDirty = false;

                        devLogger.debug(`[loadProjectDataSlim] (planner) 🏁 total ${(performance.now() - t0).toFixed(2)} ms`);
                        return projectModel.imageBuffer;
                    } catch (e) {
                        devLogger.error(`[loadProjectDataSlim] (planner) ❌ failed after ${(performance.now() - t0).toFixed(2)} ms`, e);
                    }
                } else if (projectModel.projectOrigin === "weblink") {
                    // fallback for Weblink
                    const t0 = performance.now();
                    const projectName = projectModel.weblinkOrderData?.weblinkProjectName;
                    devLogger.debug(`[loadProjectDataSlim] (weblink) ▶️ start project=${projectName} file=${projectModel?.mobileFilename}`);

                    try {
                        // --- fetch JSON ---------------------------------------------------------
                        const tFetch0 = performance.now();
                        const { data } = await this.api.getJsonFile<MobileReconstructionResponse>(projectName, projectModel?.mobileFilename);

                        if (typeof data !== "object") {
                            throw "Mobile file is not an object";
                        }

                        // --- build RG file ------------------------------------------------------
                        const tBuild0 = performance.now();
                        projectModel.mobileFile = await LoadRGFileFromServer(data, projectName || "", projectModel.projectFilename, this.api as ServerAPI);

                        // --- parse mobile file --------------------------------------------------
                        const tParse0 = performance.now();
                        const mobileFileData = loadMobileFile(projectModel.mobileFile);
                        //projectModel.plannerStep = mobileFileData.plannerStep;
                        projectModel.loadObjectsFromPatient = mobileFileData.loadObjectsFromPatient || false;
                        projectModel.segmentationInvokedByUser = mobileFileData.segmentationInvokedByUser || false;

                        // --- extract implants ---------------------------------------------------
                        const tImpl0 = performance.now();
                        projectModel.implantList = mobileFileData.implants;

                        // --- screenshot -> base64 ----------------------------------------------
                        const screenshot = mobileFileData.screenshot;
                        if (screenshot) {
                            const tShot0 = performance.now();
                            projectModel.imageBuffer = arrayToBase64(screenshot);
                        }

                        // --- finalize flags -----------------------------------------------------
                        const tFlags0 = performance.now();
                        projectModel.imageLoading = false;
                        projectModel.projectDirty = false;

                        devLogger.debug(`[loadProjectDataSlim] (weblink) 🏁 total ${(performance.now() - t0).toFixed(2)} ms`);
                        return projectModel.imageBuffer;
                    } catch (e) {
                        projectModel.errorMessage = "weblinkProjectName2: " + projectModel.weblinkOrderData + " " + (e as Error).message;
                        devLogger.error(`[loadProjectDataSlim] (weblink) ❌ failed after ${(performance.now() - t0).toFixed(2)} ms`, e);
                        error("Error while loading preview");
                    }
                }
            }

            if (!projectModel.mobileFilename) {
                try {
                    const { data: a } = await this.api.getZippedFilesList(projectModel.patientName, projectModel.projectFilename);
                    const aF = a.filter((file: GetZippedFilesListResponse) => file.fileType === "mobile.json");

                    const { data } = await this.api.getProjectFileJson<ReconstructionProjectResponse>("a", "a", "mobile", aF[0].id.toString());
                    projectModel.mobileFile = await LoadRGFileFromServer(data, projectModel.patientName, projectModel.projectFilename, this.api as ServerAPI);
                    const mobileFileData = loadMobileFile(projectModel.mobileFile);
                    projectModel.implantList = mobileFileData.implants;
                    //projectModel.plannerStep = mobileFileData.plannerStep;
                    projectModel.loadObjectsFromPatient = mobileFileData.loadObjectsFromPatient || false;
                    projectModel.segmentationInvokedByUser = mobileFileData.segmentationInvokedByUser || false;

                    const screenshot = mobileFileData.screenshot;
                    if (screenshot) {
                        projectModel.imageBuffer = arrayToBase64(screenshot);
                    }

                    devLogger.debug("2 loadProjectDataSlim projectModel ___ step 4.1:", projectModel);

                    projectModel.imageLoading = false;
                    projectModel.projectDirty = false;

                    return projectModel.imageBuffer;
                } catch (e) {
                    devLogger.debug(e);
                    projectModel.errorMessage = (e as Error).message;

                    try {
                        const { weblinkProjectName } = projectModel.weblinkOrderData;

                        const { data: a } = await this.api.getZippedFilesList(weblinkProjectName, projectModel.projectFilename);
                        const aF = a.filter((file: GetZippedFilesListResponse) => file.fileType === "mobile.json");

                        const { data } = await this.api.getProjectFileJson<ReconstructionProjectResponse>("a", "a", "mobile", aF[0].id.toString());
                        projectModel.mobileFile = await LoadRGFileFromServer(data, weblinkProjectName, projectModel.projectFilename, this.api as ServerAPI);
                        const mobileFileData = loadMobileFile(projectModel.mobileFile);
                        projectModel.implantList = mobileFileData.implants;
                        // projectModel.plannerStep = mobileFileData.plannerStep;
                        projectModel.loadObjectsFromPatient = mobileFileData.loadObjectsFromPatient || false;
                        projectModel.segmentationInvokedByUser = mobileFileData.segmentationInvokedByUser || false;

                        const screenshot = mobileFileData.screenshot;
                        if (screenshot) {
                            projectModel.imageBuffer = arrayToBase64(screenshot);
                        }

                        projectModel.imageLoading = false;
                        projectModel.projectDirty = false;

                        devLogger.debug("loadProjectData projectId projectModel END 1-1: ", projectModel);

                        return projectModel.imageBuffer;
                    } catch (e) {
                        devLogger.debug(e);
                        projectModel.errorMessage = (e as Error).message;
                        error("Error while loading preview");

                        devLogger.warn("projectModel.mobileFilename not found", e);
                    }
                }
            }
        },
        getProject(projectHash: string) {
            return this.projectsModel.find((el) => el.id === projectHash);
        },
        async getProjectByUUID(projectUUID: string) {
            const { data } = await this.api.GetProjectByUUID(projectUUID);
            if (!data) return null;

            const { name, files, patientName, ...rest } = data;
            return {
                ...rest,
                patientName: (patientName ?? name ?? "").toString(),
                projectFileList: files ?? [],
            } as ProjectModel;
        },
        async updatePatientListNotifications() {
            try {
                this.selectedPatientNotifications = [];

                if (this.selectedPatientProjects.length > 0) {
                    const patientNames = new Set(this.selectedPatientProjects.map((p) => p.patientName).filter((name) => typeof name === "string" && name.trim().length > 0));

                    for (const patientName of patientNames) {
                        const response = await this.api.getProjectNotifications(patientName);
                        const mappedNotifications = prepareNotificationsModel(patientName, response.data, this.api as ServerAPI);
                        this.selectedPatientProjects.forEach((p) => {
                            if (p.patientName === patientName) {
                                p.notifications = mappedNotifications;
                            }
                        });

                        if (patientName === this.selectedPatientFull?.name) {
                            this.selectedPatientNotifications = mappedNotifications;
                        }
                    }
                } else {
                    const selectedPatientName = this.selectedPatientFull?.name;
                    if (typeof selectedPatientName === "string" && selectedPatientName.trim().length > 0) {
                        const response = await this.api.getProjectNotifications(selectedPatientName);
                        const mappedNotifications = prepareNotificationsModel(selectedPatientName, response.data, this.api as ServerAPI);
                        this.selectedPatientNotifications = mappedNotifications;

                        this.legacyProjectsModel.forEach((p) => {
                            if (p.patientName === selectedPatientName) {
                                p.notifications = mappedNotifications;
                            }
                        });
                    }
                }

                const weblinkProjectModels = this.selectedPatientProjects.filter(
                    (p) => typeof p?.weblinkOrderData?.weblinkProjectName === "string" && p.weblinkOrderData.weblinkProjectName.trim().length > 0,
                );

                devLogger.debug("notification868 updatePatientListNotifications weblinkProjectModels: ", weblinkProjectModels);

                if (weblinkProjectModels.length > 0) {
                    const weblinkNames = new Set(
                        weblinkProjectModels
                            .map((p) => p.weblinkOrderData?.weblinkProjectName)
                            .filter((name): name is string => typeof name === "string" && name.trim().length > 0),
                    );

                    for (const weblinkProjectName of weblinkNames) {
                        const response = await this.api.getProjectNotifications(weblinkProjectName);
                        this.selectedPatientProjects.forEach((p) => {
                            if (p.weblinkOrderData?.weblinkProjectName === weblinkProjectName) {
                                p.orderNotifications = prepareNotificationsModel(weblinkProjectName, response.data, this.api as ServerAPI);
                            }
                        });
                    }
                }

                devLogger.debug("notification868 updatePatientListNotifications this.selectedPatientProjects: ", this.selectedPatientProjects);
            } catch (err) {
                exception("Error while loading preview", err as Exception);
            }
        },
        async getProjectNotifications(patientName: string) {
            try {
                const { data } = await this.api.getProjectNotifications(patientName);

                return data;
            } catch (err) {
                exception("Error while loading preview", err as Exception);
            }
        },
        async updateNotifications(patientName: string) {
            const projectModel = this.projectsModel.find((el) => el.patientName === patientName);
            if (projectModel) {
                try {
                    const { data } = await this.api.getProjectNotifications(projectModel.patientName);
                    projectModel.notifications = prepareNotificationsModel(projectModel.patientName, data, this.api as ServerAPI);
                } catch (err) {
                    exception("Error while loading preview", err as Exception);
                }
            }
        },
        async sendChatMessage(patientName: string, text: string, isPrivateMsg: boolean, channel: NotificationChannelType) {
            await this.api.addProjectMessage({
                projectName: patientName,
                text: text,
                private: isPrivateMsg,
                channel: channel,
            });
            await this.updateNotifications(patientName);
        },
        async sendMediaMessage(patientName: string, imageName: string, text: string) {
            await this.api.addProjectMediaMessage({
                projectName: patientName,
                mediaModel: {
                    imageName,
                    text,
                },
            });
            await this.updateNotifications(patientName);
        },
        async updateSeenNotifications(projectId: string, weblinkOrderId?: number) {
            const patient = this.patients.find((el) => el.patientId == projectId);

            try {
                if (patient) {
                    const projectId = weblinkOrderId ? weblinkOrderId : Number(patient.patientId);

                    const notificationTypes = [
                        NotificationChannelType.NOTIFICATION,
                        NotificationChannelType.CHAT,
                        NotificationChannelType.PRODUCTION_NOTE,
                        NotificationChannelType.SHIPPING_NOTE,
                    ];

                    for (const type of notificationTypes) {
                        await this.api.updateSeenNotifications(projectId, type);
                    }
                }
            } catch (err) {
                devLogger.error("Error: ", err);
            }
        },
        async updatePatientData(projectName: string, patientData: string) {
            try {
                await this.api.addProject(projectName, patientData);

                await this.loadPatientsSlim();
                await this.updatePatientListNotifications();
                success("Patient has been successfully updated");
            } catch (err) {
                exception("Error while updating patient: ", err as Exception);
            }
        },
        getPatientInfo(patientName: string): PatientListModel | undefined {
            const patient = this.patients.find((p) => p.patientName === patientName);
            if (patient) return patient;
        },
        async getPatientInfoByUUID(projectUUID: string): Promise<PatientListModel | undefined> {
            const project = await this.api.GetProjectByUUID(projectUUID);
            const projectData = project.data;

            if (!projectData) {
                return undefined;
            }

            return {
                dateOfBirth: projectData.patientData.dateOfBirth,
                extra: projectData.patientData,
                firstName: projectData.patientData.firstName,
                hasWeblinkOrder: true,
                importDate: projectData.createDate,
                lastModified: projectData.changeDate,
                lastName: projectData.patientData.lastName,
                lastProjectStatus: "",
                lastProjectStep: "",
                mine: projectData.mine,
                note: "",
                patientId: projectData.id.toString(),
                patientName: projectData.weblinkProjectReference,
                shares: projectData.shares,
                unreadChatCounts: [],
                unreadNotifications: 0,
                user: projectData.owner.username,
            } as PatientListModel;
        },
        getProjectFileByUUID(project: ProjectModel, projectFileHash: string) {
            return project.projectFileList.find((f) => f.uuid === projectFileHash);
        },
        getUUIDProjectId(projectId: number) {
            const patient = this.patients.find((p) => p.patientId === projectId.toString());
            return patient?.uuid;
        },
        async getPatientProjectsChanged(patientId: string) {
            const found = this.projectsModel.filter((p) => p.patientId === patientId);

            if (found.length > 0) {
                this.selectedPatientProjects = found.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
            } else {
                await this.loadPatientFullByIdSlim(Number(patientId), true);
            }
        },
        async emptyPatientAttachemtns() {
            this.selectedPatientFileModel = [];
        },
        async loadImageWithAuth(patientName: string, fileName: string): Promise<string> {
            const { url, headers } = this.api.getFileUrl(patientName, fileName); // your updated method returns {url, headers}
            let res = await fetch(url, { headers, mode: "cors" }); // NOT "no-cors"
            if (!res.ok) {
                devLogger.error("ERROR AFTER FETCH!!: ", `HTTP ${res.status}`);
                // throw new Error(`HTTP ${res.status}`);
                return "";
            }
            const blob = await res.blob();
            return URL.createObjectURL(blob); // remember to revoke() later if needed
        },
        getDocumentIconUrl(filename: string): string {
            const name = (filename || "").toLowerCase();
            if (name.endsWith(".txt")) return new URL("/img/txt_file_icon.png", import.meta.url).href;
            if (name.endsWith(".xml") || name.endsWith(".constructioninfo")) return new URL("/img/xml_file_icon.png", import.meta.url).href;
            if (name.endsWith(".pdf")) return new URL("/img/pdf_file_icon.png", import.meta.url).href;
            if (name.endsWith(".docx")) return new URL("/img/docx_file_icon.png", import.meta.url).href;
            if (name.endsWith(".doc")) return new URL("/img/doc_file_icon.png", import.meta.url).href;
            if (name.endsWith(".xlsx")) return new URL("/img/xlsx_file_icon.png", import.meta.url).href;
            if (name.endsWith(".xls")) return new URL("/img/xls_file_icon.png", import.meta.url).href;
            if (name.endsWith(".zip")) return new URL("/img/zip_file_icon.png", import.meta.url).href;
            return new URL("/img/pdf_file_icon.png", import.meta.url).href;
        },
        async addObjectFromWs(eventFile, event) {
            const found = this.projectFileModel.filter((p) => p.patientId === event.data.id.toString());

            const patientId = event.data.name;

            const fileModel: ImageFileModel = {
                id: eventFile.id,
                projectFileUuid: eventFile.uuid,
                lastModified: new Date(eventFile.file ? eventFile.file.changeDate : Date.now()),
                patientId: patientId,
                //user: file.uploadedBy.name,
                filename: eventFile.name,
                //itemImageSrc: this.api.getFileUrl(patientId, eventFile.name),
                itemImageSrc:
                    eventFile.type !== ProjectFileType.STL ? await this.loadImageWithAuth.call(this, patientId, eventFile.name) : this.api.getFileUrl(patientId, eventFile.name),
                type: eventFile.type,
                thumbnailImageSrc: "",
                alt: "",
                title: "",
            };

            const pushAttachment = <T>(arr: T[], item: T, keyFn: (x: T) => string): void => {
                const key = keyFn(item);
                const idx = arr.findIndex((x) => keyFn(x) === key);
                if (idx === -1) {
                    arr.push(item);
                } else {
                    arr[idx] = { ...arr[idx], ...item };
                }
            };

            const modelKey = (m: ImageFileModel) => `${m.type}:${m.filename}`;
            let previewUrl = "";

            const isSvgImage = (name?: string) => (name || "").toLowerCase().endsWith(".svg");
            let previewName = eventFile.name;
            if (fileModel.type !== ProjectFileType.Image && fileModel.type !== ProjectFileType.Document) {
                previewName = eventFile.name + ".thumb";
            } else if (fileModel.type === ProjectFileType.Image && isSvgImage(eventFile.name)) {
                previewName = eventFile.name + ".thumb";
            }
            let thumbnailLoadFailed = false;

            const isDocument = fileModel.type === ProjectFileType.Document || fileModel.type === ProjectFileType.LiabilityForm;
            if (isDocument) {
                previewUrl = this.getDocumentIconUrl(fileModel.filename);
                thumbnailLoadFailed = false;
            } else {
                try {
                    previewUrl = await this.loadImageWithAuth.call(this, patientId, previewName);

                    if (!previewUrl && fileModel.type === ProjectFileType.Image && isSvgImage(eventFile.name)) {
                        previewUrl = await this.loadImageWithAuth.call(this, patientId, eventFile.name);
                    }

                    thumbnailLoadFailed = !previewUrl;
                } catch (e) {
                    devLogger.debug("addObjectFromWs previewName previewUrl: ", e);
                    thumbnailLoadFailed = true;
                }
            }

            pushAttachment(
                this.projectFileModel,
                {
                    ...fileModel,
                    type: eventFile.type,
                    name: eventFile.name,
                    thumbnailImageSrc: previewUrl,
                    previewUrl,
                    thumbnailLoadFailed,
                } as ImageFileModel,
                modelKey,
            );

            this.selectedPatientFileModel = this.projectFileModel.filter((p) => p.patientId === patientId);
        },
        async loadPatientAttachments(patientId: string) {
            const found = this.attachmentsFileModel.filter((p) => p.patientId === patientId);
            const foundProjectFileModel = this.projectFileModel.filter((p) => p.patientId === patientId);

            const hasMissingThumbnails = this.selectedPatientFileModel.some((item: ImageFileModel) => {
                const src = item.thumbnailImageSrc;
                const thumbnailLoadFailed = (item as any).thumbnailLoadFailed;
                return !thumbnailLoadFailed && (typeof src !== "string" || src.trim() === "");
            });

            if (foundProjectFileModel.length > 0 && !hasMissingThumbnails) {
                this.selectedPatientFileModel = foundProjectFileModel;
                return foundProjectFileModel;
            }

            this.selectedPatientFileModel = [];

            for (const file of found) {
                const fileModel: ImageFileModel = {
                    id: file.id,
                    projectFileUuid: file.uuid,
                    lastModified: new Date(file.file ? file.file.changeDate : Date.now()),
                    patientId: patientId,
                    //user: file.uploadedBy.name,
                    filename: file.name,
                    itemImageSrc: this.api.getFileUrl(patientId, file.name),
                    type: file.type,
                    thumbnailImageSrc: "",
                    alt: "",
                    title: "",
                };

                const pushAttachment = <T>(arr: T[], item: T, keyFn: (x: T) => string): void => {
                    // Extract both key and patientId for comparison
                    const itemKey = keyFn(item);
                    const itemPatientId = (item as any).patientId;

                    const idx: number = arr.findIndex((x: T) => {
                        const sameKey = keyFn(x) === itemKey;
                        const samePatient = (x as any).patientId === itemPatientId;
                        return sameKey && samePatient;
                    });

                    if (idx === -1) {
                        arr.push(item);
                    } else {
                        arr[idx] = { ...arr[idx], ...item };
                    }
                };

                const modelKey = (m: ImageFileModel) => `${m.type}:${m.filename}`;
                if (file.type === ProjectFileType.STL) {
                    let previewUrl = await this.loadImageWithAuth.call(this, patientId, file.name + ".thumb");
                    if (!previewUrl) {
                        previewUrl = await this.loadImageWithAuth.call(this, patientId, replaceSuffix(file.name, ".png"));
                    }
                    const thumbnailLoadFailed = !previewUrl;

                    pushAttachment(
                        this.projectFileModel,
                        {
                            ...fileModel,
                            type: file.type,
                            name: file.name,
                            thumbnailImageSrc: previewUrl,
                            previewUrl,
                            thumbnailLoadFailed,
                        } as ImageFileModel,
                        modelKey,
                    );
                }

                if (file.type === ProjectFileType.Image) {
                    const isSvg = (file.name || "").toLowerCase().endsWith(".svg");
                    let previewUrl = isSvg ? await this.loadImageWithAuth.call(this, patientId, file.name + ".thumb") : "";
                    if (!previewUrl) {
                        previewUrl = await this.loadImageWithAuth.call(this, patientId, file.name);
                    }
                    const thumbnailLoadFailed = !previewUrl;

                    pushAttachment(
                        this.projectFileModel,
                        {
                            ...fileModel,
                            type: ProjectFileType.Image,
                            name: file.name,
                            thumbnailImageSrc: previewUrl,
                            previewUrl,
                            thumbnailLoadFailed,
                            allowDownload: true,
                        } as ImageFileModel,
                        modelKey,
                    );
                }

                if (file.type === ProjectFileType.Document) {
                    pushAttachment(
                        this.projectFileModel,
                        {
                            ...fileModel,
                            type: ProjectFileType.Document,
                            name: file.name,
                            thumbnailImageSrc: this.getDocumentIconUrl(file.name),
                            allowDownload: true,
                        } as ImageFileModel,
                        modelKey,
                    );
                }

                this.selectedPatientFileModel = this.projectFileModel.filter((p) => p.patientId === patientId);
            }

            devLogger.debug("loadPatientAttachments projectFileModel patientId END: ", patientId);

            this.selectedPatientFileModel = this.projectFileModel.filter((p) => p.patientId === patientId);
        },
        async loadPatientFullByIdSlim(patientId: number, projectDirty: boolean = false) {
            try {
                const pcStore = useProductionCenterStore();

                const start = performance.now();

                const patientProjects = this.projectsModel.filter((p) => p.patientId === patientId.toString());

                if (patientProjects && !projectDirty) return; // add any custom condition for not updating projects

                const { data } = await this.api.getPatientFullById(patientId);
                this.selectedPatientFull = data;

                if (!this.selectedPatientFull) return;

                this.projectsModel = this.projectsModel.filter((p) => p.patientId !== patientId.toString()); // clean projectModel if dirty

                const plannerProjectUUID = this.getUUIDProjectId(this.selectedPatientFull?.id || 0);
                const patientProjectFilesInOrders = this.selectedPatientFull.orders.map((o) => o.originProjectFileReference);

                const vendorName = await this.ensureVendorName();
                let plannerProjectFiles = this.selectedPatientFull.files
                    .filter((file: FileElement) => file.type === ProjectFileType.Project && !patientProjectFilesInOrders.includes(file.name))
                    .map((file: FileElement) => {
                        const { versionCompany, format } = extractProjectMetadataExtras(file.metadata, vendorName ?? undefined);
                        return {
                            ...file,
                            id: buildProjectKey(this.selectedPatientFull?.name || "", file.name, file.uuid, file.id),
                            uuid: file.uuid,
                            projectUUID: plannerProjectUUID,
                            patientId: this.selectedPatientFull?.id.toString(),
                            patientName: this.selectedPatientFull?.name,
                            projectFilename: file.name,
                            projectDisplayName: file.name,
                            mobileFilename: file.name + ".mobile.json",
                            lastModified: new Date(file.file.changeDate),
                            user: file.uploadedBy.name,
                            imageLoading: true,
                            projectFileId: file.id,
                            projectFileUuid: file.uuid,
                            downloadFilename: file.name,
                            projectDirty: true,
                            projectFileList: this.selectedPatientFull?.files,
                            isJsonProject: true,
                            segmentationStatus: SegmentationStatus.UNKNOWN,
                            projectOrigin: "planner",
                            versionCompany,
                            format,
                        };
                    }) as unknown as ProjectModel[];

                const _legacyProjects = plannerProjectFiles.filter((project: ProjectModel) => project.format === "RealGUIDE 5");
                this.legacyProjectsModel.push(..._legacyProjects);

                if (_legacyProjects.length > 0) {
                    const patient = this.patients.find((p) => p.patientName === _legacyProjects[0].patientName);
                    patient.hasLegacyProject = true;
                }

                plannerProjectFiles = plannerProjectFiles.filter((project: ProjectModel) => project.format !== "RealGUIDE 5");
                this.projectsModel.push(...plannerProjectFiles);

                const weblinkOrders = this.selectedPatientFull.orders;
                let resetOrderNotifications = true;
                // Get project revisions (from weblink orders)
                for (const order of weblinkOrders) {
                    const { data } = await this.api.getPatientFullById(order.projectId);
                    const orderProjects = data.files;

                    const weblinkProjectUUID = order.projectUuid;

                    const unreadOrderNotifications = Object.values(data.unreadNotificationsPerChannel ?? {}).reduce((sum, count) => sum + (count ?? 0), 0);

                    const patient = this.patients.find((p) => p.patientName === order.originProjectReference);
                    if (patient) {
                        patient.hasWeblinkOrder = true;
                    }

                    if (unreadOrderNotifications) {
                        if (!patient) return;

                        if (resetOrderNotifications) {
                            patient.orderUnreadNotificationsPerChannel = 0;
                            resetOrderNotifications = false;
                        }

                        patient.orderUnreadNotificationsPerChannel += unreadOrderNotifications;
                    }

                    const attachmentPreviewNames = new Set(
                        orderProjects
                            .filter((file: FileElement) => file.type === ProjectFileType.ProjectAttachmentPreview)
                            .map((file: FileElement) => normalizeAttachmentBaseName(file.name)),
                    );

                    const weblinkOrderProjects = orderProjects
                        .filter((file: FileElement) => file.type === ProjectFileType.ProjectAttachment && attachmentPreviewNames.has(normalizeAttachmentBaseName(file.name)))
                        .map((file: FileElement) => {
                            const { versionCompany, format } = extractProjectMetadataExtras(file.metadata, vendorName ?? undefined);
                            return {
                                ...file,
                                projectOrigin: "weblink",
                                id: buildProjectKey(this.selectedPatientFull?.name || "", file.name, file.uuid, file.id, order.projectId?.toString()),
                                uuid: file.uuid,
                                projectUUID: weblinkProjectUUID,
                                patientId: this.selectedPatientFull?.id.toString(),
                                patientName: this.selectedPatientFull?.name,
                                projectFilename: file.name,
                                projectDisplayName: file.name,
                                mobileFilename: file.name + ".mobile.json",
                                lastModified: new Date(file.file.changeDate),
                                user: file.uploadedBy.name,
                                imageLoading: true,
                                projectFileId: file.id,
                                projectFileUuid: file.uuid,
                                downloadFilename: file.name,
                                projectDirty: true,
                                projectFileList: this.selectedPatientFull?.files,
                                isJsonProject: true,
                                segmentationStatus: SegmentationStatus.UNKNOWN,
                                versionCompany,
                                format,
                                weblinkOrderData: {
                                    projectId: order.projectId,
                                    weblinkProjectName: order.projectName,
                                    weblinkOrderId: order.id,
                                    caseNumber: order.caseNumber ?? `${order.id} (${translation("weblink.caseNumberNotSet")})`,
                                    productionCenterId: order.productionCenterId,
                                    productionCenterName: pcStore.getProductionCenterName(order.productionCenterId),
                                    step: order.step,
                                    status: order.status,
                                    statusColor: JSON.parse(order.statusData || "{}").color,
                                    allowsUserEditing: order.allowsUserEditing,
                                    awaitUserConfirmation: order.awaitUserConfirmation,
                                    originProjectFileReference: order.originProjectFileReference,
                                    originProjectFileReferenceId: this.selectedPatientFull?.files.find((f) => f.name === order.originProjectFileReference)?.id,
                                    revisionPfId: order.revisionPfId,
                                    unreadNotificationsPerChannel: unreadOrderNotifications,
                                },
                            };
                        }) as ProjectModel[];

                    plannerProjectFiles.push(...weblinkOrderProjects);

                    this.projectsModel.push(...weblinkOrderProjects);
                }

                this.selectedPatientProjects = plannerProjectFiles
                    .filter((p) => p.type !== ProjectFileType.STL)
                    .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
                this.selectedPatientAttachemnts = plannerProjectFiles.filter((p) => p.type === ProjectFileType.STL);

                const attachmentExcludedTypes = new Set(["processing-data", "mobile", "project"]);
                const attachmentKey = (file: FileElement) => file.uuid ?? file.id ?? `${file.type}:${file.name}`;
                const existingAttachmentKeys = new Set(this.attachmentsFileModel.map((file) => attachmentKey(file as FileElement)));

                this.attachmentsFileModel.push(
                    ...this.selectedPatientFull.files
                        .filter((file) => !attachmentExcludedTypes.has(file.type as string) && !existingAttachmentKeys.has(attachmentKey(file)))
                        .map((file) => ({
                            ...file,
                            patientId: this.selectedPatientFull?.name,
                        })),
                );

                await this.updatePatientListNotifications();

                const end = performance.now(); // second time spot
                const duration = end - start;

                devLogger.debug(`1 loadPatientFullByIdSlim Execution time: ${duration.toFixed(3)} ms: `);
            } catch (err) {}
        },
        async getStlFile(patientName: string, filename: string) {
            try {
                const { data } = await this.api.getFile<ArrayBuffer>(patientName, filename);
                const dataInt8Array = new Int8Array(data);

                success("STL loaded successfully");
                return dataInt8Array;
            } catch (err) {
                exception("Error while getting STL: ", err as Exception);
            }
        },
        async uploadFile(patientName: string, filename: string, type: ProjectFileType, file: CustomFile, progressCallback?: (progress: number) => void) {
            try {
                await this.api.uploadFile(patientName, filename, type, file, progressCallback);

                const fileModel: ImageFileModel = {
                    id: file.id,
                    lastModified: new Date(file.changeDate),
                    patientId: patientName,
                    //user: file.uploadedBy.name,
                    filename: filename,
                    itemImageSrc: this.api.getFileUrl(patientName, file.name),
                    type: type,
                    thumbnailImageSrc: "",
                    alt: "",
                    title: "",
                };

                if (type === ProjectFileType.STL) {
                    const preview = filename + ".thumb";
                    let previewUrl = "";
                    if (preview) {
                        previewUrl = this.api.getFileUrl(patientName, preview);
                    }

                    const tmp = this.projectsModel.find((p) => p.patientName === patientName);
                    if (tmp) {
                        tmp.projectAttachments?.push({
                            ...fileModel,
                            thumbnailImageSrc: previewUrl,
                            previewUrl: previewUrl,
                            allowDownload: true,
                        } as ImageFileModel);
                    }
                    // this.projectFileModel.push({
                    //     ...fileModel,
                    //     thumbnailImageSrc: previewUrl,
                    //     previewUrl: previewUrl,
                    // } as ImageFileModel);
                }
                if (type === ProjectFileType.Image) {
                    const tmp = this.projectsModel.find((p) => p.patientName === patientName);
                    if (tmp) {
                        const isSvg = (filename || "").toLowerCase().endsWith(".svg");
                        const previewUrl = isSvg ? this.api.getFileUrl(patientName, filename + ".thumb") : fileModel.itemImageSrc;
                        tmp.projectAttachments?.push({
                            ...fileModel,
                            thumbnailImageSrc: previewUrl,
                            previewUrl,
                            allowDownload: true,
                        } as ImageFileModel);
                    }
                    // this.projectFileModel.push({
                    //     ...fileModel,
                    //     thumbnailImageSrc: fileModel.itemImageSrc,
                    //     previewUrl: fileModel.itemImageSrc,
                    //     allowDownload: true,
                    // } as ImageFileModel);
                }
                if (type == ProjectFileType.Document) {
                    const tmp = this.projectsModel.find((p) => p.patientName === patientName);
                    if (tmp) {
                        tmp.projectAttachments?.push({
                            ...fileModel,
                            thumbnailImageSrc: new URL("/img/pdf_file_icon.png", import.meta.url).href,
                            previewUrl: fileModel.itemImageSrc,
                            allowDownload: true,
                        } as ImageFileModel);
                    }
                    // this.projectFileModel.push({
                    //     ...fileModel,
                    //     thumbnailImageSrc: new URL("/img/pdf-icon.png", import.meta.url).href,
                    //     allowDownload: true,
                    // } as ImageFileModel);
                }

                // return progressCallback?.(progress);
            } catch (err) {
                //toast.add({ severity: "error", summary: "Error while uploading file", detail: err as Exception, life: 3000 });
                exception("Error while uploading file: ", err as Exception);
            }
        },
        async copyFileBetweenProjects(model: CopyFileRequestModel): Promise<number | undefined> {
            try {
                const { data } = await this.api.copyFileBetweenProjects(model);
                return data.fileId;
            } catch (err) {
                exception("Error while copying file between projects:", err as Exception);
            }
        },
        async shareProject(model: ProjectShareModel) {
            try {
                const { data } = await this.api.shareProject(model);
                return data;
            } catch (err) {
                exception("Error while sharing project:", err as Exception);

                return err;
            }
        },
        async setProjectOwner(model: SetProjectOwnerPayload) {
            try {
                const { data } = await this.api.setProjectOwner(model);
                return data;
            } catch (err) {
                exception("Error while setting project owner:", err as Exception);
                throw err;
            }
        },
        async setProjectFileEditableAsPro(model: SetProjectFileEditableAsProRequest) {
            try {
                const { data } = await this.api.setProjectFileEditableAsPro(model);
                return data;
            } catch (err) {
                exception("Error while setting project file editable as pro:", err as Exception);
                throw err;
            }
        },
        async shareProjectFile(model: ProjectFileShareModel) {
            try {
                const { data } = await this.api.shareProjectFile(model);
                const project = this.selectedPatientProjects.find((p) => p.name === model.fileName);

                if (project) {
                    const updatedModel = {
                        ...model,
                        withUser: model.withUsername
                            ? {
                                  username: model.withUsername,
                                  name: model.withUsername,
                                  colorCode: "#96666b",
                              }
                            : null,
                    };
                    project.shares.push(updatedModel);
                }

                return data;
            } catch (err) {
                exception("Error while sharing project file:", err as Exception);

                return err;
            }
        },
        async getProjectFileMetaDataById(id: number) {
            try {
                const { data } = await this.api.getProjectFileMetaDataById(id);
                return data;
            } catch (err) {
                exception("Error while pulling project metadata:", err as Exception);
            }
        },
        async getUserDoctorList() {
            try {
                this.doctors = (await this.api.getUsersDoctorList()).data;
            } catch (err) {
                exception("Error while getting doctor list: ", err as Exception);
            }
        },
        async upsertDoctor(model: DoctorModel) {
            try {
                await this.api.upsertDoctor(model);

                await this.getUserDoctorList();
                await this.loadPatientsSlim();
            } catch (err) {
                exception("Error while adding doctor: ", err as Exception);
            }
        },
        async unAssignPatient(patients: PatientListModel[]) {
            try {
                for (const patient of patients) {
                    const requestData = {
                        notes: patient.note,
                        dateOfBirth: patient.dateOfBirth,
                        extra: {
                            patientName: patient.extra.patientName,
                            patientID: patient.extra.patientID,
                            modality: patient.extra.modality,
                            doctorID: "0",
                            study_instance_UID: patient.extra.study_instance_UID,
                        },
                    };
                    await this.api.addProject(patient.patientName, JSON.stringify(requestData));
                }
            } catch (err) {
                exception("Error while unassigning patients: ", err as Exception);
            }
        },
        async deleteDoctor(id: number) {
            try {
                await this.api.deleteDoctor(id);

                this.doctors = this.doctors.filter((d) => d.id !== id);

                await this.loadPatientsSlim();
            } catch (err) {
                exception("Error while deleting doctor: ", err as Exception);
            }
        },
        async getProjectFileList(projectName: string) {
            try {
                const { data } = await this.api.getProjectFileList(projectName);
                return data;
            } catch (err) {
                exception("Error while getting project files: ", err as Exception);
            }
        },
        async addProject(projectName: string, patientData: string, projectType: ProjectType, weblinkProjectReference?: string, weblinkProjectFileReference?: string) {
            try {
                const { data } = await this.api.addProject(projectName, patientData, projectType, weblinkProjectReference, weblinkProjectFileReference);

                return data.data;
            } catch (err) {
                exception(`${translation("shared.error")}: `, err as Exception);
            }
        },
        async deleteProjectFile(patientName: string, filename: string) {
            try {
                await this.api.deleteProjectFile(patientName, filename);
                success(translation("web-rg.projectFileDeleted", { filename }));
            } catch (err) {
                exception("Error while deleting project file: ", err as Exception);
            }
        },
        async deleteProjectBatch(projectNames: string[]) {
            try {
                await this.api.deleteProjectBatch(projectNames);
                success(translation("web-rg.projectsDeleted"));
            } catch (err) {
                exception("Error while deleting project batch: ", err as Exception);
            }
        },
        async addProjectPrescriptionType(model: ProjectPresctiptionTypeRequestModel) {
            try {
                return await this.api.addProjectPrescriptionType(model);
            } catch (err) {
                exception("Error: ", err as Exception);

                return err.response.data;
            }
        },
    },
});
